import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, StateKey, TransferState } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpOptions, RestApiService } from '../rest-api/rest-api.service';
import {
  AboutUs,
  AuthStatus,
  CategoryTree,
  Client,
  LanguageRegionMap,
  MachineBaseDto,
  MachineListDto,
  PrivatePortalsObject,
  PrivatePortalsPageObject,
  QueryParams,
  SearchFilter,
  SearchFilters2,
  SellToUsDto,
  SellVehicle,
  ShippingDto,
  UserSignupDto,
} from '../../../types';
import { SharedService } from '../shared-service/shared.service';
import { DOCUMENT } from '@angular/common';
import { ClientStylesService } from '../client-styles/client-styles.service';
import { languageRegionCodesMap } from '../../constants';

const ClientKey = makeStateKey('client') as StateKey<Client>;

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private serviceUrl = 'client/';
  private clientWebServiceUrl = 'client-web/';
  private backend_url: string = environment.apiUrl;
  private private_portal_url: string = environment.apiPrivate;
  public client!: Client;
  public hostName: string;
  public fallbackLanguage: LanguageRegionMap = 'en';
  clientLoaded$ = new BehaviorSubject<boolean>(false);
  currentClientState = new BehaviorSubject<Client | null>(null);
  currentSharedClientState = this.currentClientState.asObservable();

  currentCookieState = new BehaviorSubject<any>(null);
  currentSharedCookieState = this.currentCookieState.asObservable();

  updateSharedClientState(currentClientState: Client) {
    this.currentClientState.next(currentClientState);
  }

  updateSharedCookieState(currentCookieState: any, type: any) {
    this.currentCookieState.next({ currentCookieState, type });
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private restApiService: RestApiService,
    private http: HttpClient,
    private sharedService: SharedService,
    private clientStyles: ClientStylesService,
    private tstate: TransferState,
  ) {
    if (this.sharedService.isBrowser()) {
      environment.host
        ? (this.hostName = environment.host)
        : (this.hostName = this.document.location.hostname);
    } else {
      this.hostName = this.sharedService.getRequestHostName();
      // console.log("Server hostName in app component:", this.hostName);
    }
  }

  public setClientV2 = async (options?: RequestInit): Promise<void> => {
    try {
      const response = await fetch(
        `${this.backend_url}/v2/client/${this.hostName}`,
        options
      );
      
      const data: Client = await response.json();
      //console.log(data);
      if(response.status === 400) {
        throw ('Unavailable');
      }
      this.client = data;
       //this.client = { ...data, layout: 'kbauto' };
    } catch (error) {
      console.log('Error fetching client data');
    }
  };

  public signUp = async (
    dto: Partial<UserSignupDto>,
    token: string,
    options?: RequestInit
  ): Promise<void> => {
    try {
      if (!token) {
        throw new Error('User is not authenticated. No access token found.');
      }

      const response = await fetch(`${this.backend_url}/client/sign-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options?.headers ?? {}),
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        let errorData: any;

        try {
          errorData = await response.json(); // ✅ Try parsing JSON error
        } catch {
          errorData = { message: await response.text() }; // ✅ Fallback to text
        }

        throw new Error(
          JSON.stringify({
            status: response.status,
            error: errorData,
          })
        );
      }

      console.log('✅ Successfully signed up with provider');

      const data = await response.status;
      console.log('✅ data', data);
    } catch (error) {
      console.error('🔴 Error during sign-up:', error);

      // ✅ If error is JSON string, parse it before rethrowing
      // @ts-ignore
      if (typeof error?.message === 'string') {
        try {
          // @ts-ignore
          const parsedError = JSON.parse(error.message);
          throw parsedError;
        } catch {
          throw error; // If parsing fails, throw original error
        }
      } else {
        throw error; // Propagate the error
      }
    }
  };

  public requestAccess = async (
    token: string,
    options?: RequestInit,
    data: {[key:string]: any} = {},
  ): Promise<void> => {
    try {
      if (!token) {
        throw new Error('User is not authenticated. No access token found.');
      }

      const opts:RequestInit = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options?.headers ?? {}),
        },
      };

      if (data) {
        opts.body = JSON.stringify(data);
      }

      const response = await fetch(
        `${this.backend_url}/client/${this.hostName}/request-access`,
        opts
      );

      if (!response.ok) {
        const errorText = await response.text(); // Read response body in case of failure
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }
    } catch (error) {
      console.error('🔴 Error requesting access:', error);

      // ✅ If error is JSON string, parse it before rethrowing
      // @ts-ignore
      if (typeof error?.message === 'string') {
        try {
          // @ts-ignore
          const parsedError = JSON.parse(error.message);
          throw parsedError;
        } catch {
          throw error; // If parsing fails, throw original error
        }
      } else {
        throw error; // Propagate the error
      }
    }
  };

  public authStatus = async (
    options?: RequestInit
  ): Promise<AuthStatus | undefined> => {
    try {
      const response = await fetch(
        `${this.backend_url}/client/${this.hostName}/auth-status`,
        options
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: AuthStatus = await response.json();
      return data; // ✅ Ensure data is returned
    } catch (error) {
      //console.log('🔴 Error fetching AuthStatus:', error);
    }

    return undefined; // ✅ Explicitly return undefined in case of failure
  };

  public getMachineDetailsV2 = async (
    macId: string,
    options?: RequestInit
  ): Promise<MachineBaseDto> => {
    const response = await fetch(
      `${this.backend_url}/client/${this.hostName}/v2/machine/${macId}`,
      options
    );
    const data: MachineBaseDto = await response.json();
    if(response.status === 404) {
      throw (response.status);
    }
    return data;
  };

  public getMachineNextOrPrev = async (
    macId: string,
    value: 'NEXT' | 'PREVIOUS',
    options?: RequestInit
  ): Promise<MachineBaseDto> => {
    const response = await fetch(
      `${this.backend_url}/client/${this.hostName}/machine/${macId}/related?prefer=${value}`,
      options
    );
    if(response.status === 404) {
      throw (response.status);
    }
    const data: MachineBaseDto = await response.json();
    return data;
  };

  public getShipping = async (
    macId: string,
    currency: string,
    options?: RequestInit
  ): Promise<ShippingDto> => {
    const response = await fetch(
      `${this.backend_url}/client/${this.hostName}/v2/machine/${macId}/shipping?currency=${currency}`,
      options
    );
    const data: ShippingDto = await response.json();
    return data;
  };

  getClientHomePage(client?: any): Observable<any> {
    return this.restApiService.get(
      `${this.serviceUrl}${client || this.hostName}/homepage`
    );
  }

  public getClientPrivatePortalPage = async (
    client?: string,
    options?: RequestInit
  ): Promise<PrivatePortalsPageObject> => {

    const response = await fetch(
      `${this.backend_url}/client/${this.hostName}/private-portals`
    );

    const data: PrivatePortalsPageObject = await response.json();
    return data;
  }

  public getClientMarketplaces = async (
    token: string,
    options?: RequestInit
  ): Promise<PrivatePortalsObject[]> => {
    if (!token) {
      throw new Error('User is not authenticated. No access token found.');
    }
    const response = await fetch(
      `${this.private_portal_url}/client/${this.hostName}/marketplaces`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options?.headers ?? {}),
        },
      }
    );
    const data: PrivatePortalsObject[] = await response.json();
    return data;
  };

  getClientSellVehiclePage(client?: string): Observable<SellVehicle> {
    return this.restApiService.get<SellVehicle>(
      `${this.serviceUrl}${client || this.hostName}/sell-vehicle`
    );
  }

  postSellVehicleForm(
    client?: string,
    data?: SellToUsDto
  ): Observable<unknown> {
    return this.restApiService.post(
      `${this.serviceUrl}${client || this.hostName}/sell-vehicle`,
      data
    );
  }

  getClientAboutUsPage(client?: string): Observable<AboutUs> {
    return this.restApiService.get<AboutUs>(
      `${this.serviceUrl}${client || this.hostName}/about-us`
    );
  }

  getClientContactPage(client?: any): Observable<any> {
    return this.restApiService.get(
      `${this.serviceUrl}${client || this.hostName}/contact`
    );
  }

  public getClientTree = async (
    options?: RequestInit
  ): Promise<CategoryTree> => {
    const response = await fetch(
      `${this.backend_url}/client/${this.hostName}/category-tree`,
      options
    );
    const data: CategoryTree = await response.json();
    return data;
  };

  public getClientSearchFiltersV2 = async (
    lang: string,
    queryParams: QueryParams = {}, // Default to an empty object
    options?: RequestInit
  ): Promise<SearchFilter[]> => {
    // Ensure queryParams is a valid object and include the `language` parameter
    const searchParams = new URLSearchParams({ language: lang });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    const url = `${this.backend_url}/client/${this.hostName}/v2/search-filters${queryString}`;

    const response = await fetch(url, options);

    const data: SearchFilter[] = await response.json();
    return data;
  };

  public getMachineListV2Url(
    lang: string,
    queryParams: QueryParams = {}
  ): string {
    const searchParams = new URLSearchParams({ language: lang });
    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    return `${this.backend_url}/client/${this.hostName}/v2/search${queryString}`;
  }

  public getNewClientSearchFiltersV2Url(
    lang: string,
    queryParams: QueryParams = {}
  ): string {
    const searchParams = new URLSearchParams({ language: lang });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    return `${this.backend_url}/client/${this.hostName}/search-filters2${queryString}`;
  }

  public getClientSearchFiltersV2Url(
    lang: string,
    queryParams: QueryParams = {}
  ): string {
    const searchParams = new URLSearchParams({ language: lang });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    return `${this.backend_url}/client/${this.hostName}/v2/search-filters${queryString}`;
  }

  public getNewClientSearchFiltersV2 = async (
    lang: string,
    queryParams: QueryParams = {}, // Default to an empty object
    options?: RequestInit
  ): Promise<SearchFilters2[]> => {
    // Ensure queryParams is a valid object and include the `language` parameter
    const searchParams = new URLSearchParams({ language: lang });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    const url = `${this.backend_url}/client/${this.hostName}/search-filters2${queryString}`;

    const response = await fetch(url, options);
    const data: SearchFilters2[] = await response.json();
    return data;
  };

  public getMachineListV2 = async (
    lang: string,
    queryParams: QueryParams = {}, // Default to an empty object
    options?: RequestInit
  ): Promise<MachineListDto> => {
    // Ensure queryParams is a valid object and include the `language` parameter
    const searchParams = new URLSearchParams({ language: lang });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => searchParams.append(key, String(val)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    // Construct the final URL with query parameters
    const queryString = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';
    const url = `${this.backend_url}/client/${this.hostName}/v2/search${queryString}`;

    const response = await fetch(url, options);
    const machines: MachineListDto = await response.json();
    return machines;
  };

  contactClient(json: any, client?: any) {
    return this.restApiService.post(
      `${this.serviceUrl}${client || this.hostName}/contact-client`,
      json
    );
  }

  subscribeToClient(email: any, client?: any) {
    return this.restApiService.post(
      `${this.serviceUrl}${client || this.hostName}/web-subscriber/${email}`,
      ''
    );
  }

  getTermsPage(client?: any, lang?: any): Observable<any> {
    return this.http.get(
      `${this.backend_url}/${this.serviceUrl}${
        client || this.hostName
      }/${lang}/terms`,
      { responseType: 'text' }
    );
  }

  getPageContent(client?: any, pageUrl?: any): Observable<any> {
    return this.http.get(
      `${this.backend_url}/${this.serviceUrl}${
        client || this.hostName
      }/${pageUrl}`
    );
  }

  load() {
    return new Promise(async (resolve, reject) => {
      // Extract the locale from the URL
      const url = new URL(this.document.location.href);
      const pathSegments = url.pathname.split('/').filter(Boolean); // Split path and remove empty segments
      const [extractedLocale] = pathSegments; // First segment of the path
      const client = this.tstate.get(ClientKey, undefined);
      if (client) {
        this.client = client;
        this.clientStyles.set(this.client, extractedLocale);
        this.clientLoaded$.next(true);
        resolve(true);
      } else {
        // Extract IETF BCP 47 tag
        let regionCode: string | undefined;
        if (extractedLocale) {
          try {
            regionCode =
              languageRegionCodesMap[extractedLocale as LanguageRegionMap];
          } catch {}
        }
  
        // Don't add header if no tag was extract, server will respond with default language
        const headers = regionCode
          ? { headers: { 'Accept-Language': regionCode } }
          : {};
  
        // TODO: Proper error handling, such as retries
        try {
  
          const response = await this.setClientV2(headers);
          this.clientStyles.set(this.client, extractedLocale);
          this.clientLoaded$.next(true);
          this.tstate.set(ClientKey, this.client);
          resolve(true);
        } catch (error) {
          resolve(true);
          console.log('Error fetching client data');
        }
      }
    });
  }
}
